import type { InquiryInterest } from './inquiryInterest';

/**
 * ### INQUIRY_CTA_SOURCE_VALUES
 *
 * Fixed `source` values a call-to-action may attach to an inquiry link.
 * Case pages use the {@link industrialCaseCtaSource} pattern instead of a fixed value.
 * The prefix before the first hyphen names the solution the visitor came from, which is what
 * {@link solutionIdForInquiryCtaSource} reads.
 */
export const INQUIRY_CTA_SOURCE_VALUES = [
  'home-claim',
  'industrial-poc',
  'industrial-audio-review',
  'safety-cta',
  'cases-list',
] as const;

/**
 * ### INDUSTRIAL_CASE_CTA_SOURCE_PREFIX
 *
 * Prefix of the `source` value sent by a customer case page, followed by the case slug.
 */
export const INDUSTRIAL_CASE_CTA_SOURCE_PREFIX = 'industrial-case-';

/**
 * Case slugs are lowercase alphanumerics separated by single hyphens, matching the
 * slug rule of the industrial case document.
 */
const industrialCaseCtaSourcePattern = new RegExp(
  `^${INDUSTRIAL_CASE_CTA_SOURCE_PREFIX}[a-z0-9]+(?:-[a-z0-9]+)*$`,
);

/**
 * ### IndustrialCaseCtaSource
 *
 * `source` value of a customer case page, carrying the case slug after the prefix.
 */
export type IndustrialCaseCtaSource =
  `${typeof INDUSTRIAL_CASE_CTA_SOURCE_PREFIX}${string}`;

/**
 * ### InquiryCtaSource
 *
 * Any `source` value a call-to-action is allowed to send to the inquiry page.
 */
export type InquiryCtaSource =
  (typeof INQUIRY_CTA_SOURCE_VALUES)[number] | IndustrialCaseCtaSource;

/**
 * ### SolutionId
 *
 * Analytics identifier of a solution, attached to lead events as `solution_id`.
 */
export type SolutionId = 'listen_ai_industrial' | 'listen_ai_safety';

/**
 * ### industrialCaseCtaSource
 *
 * Builds the `source` value for a customer case page.
 *
 * @param caseSlug Slug of the case whose page hosts the call-to-action
 * @returns The case-specific source value
 */
export function industrialCaseCtaSource(
  caseSlug: string,
): IndustrialCaseCtaSource {
  return `${INDUSTRIAL_CASE_CTA_SOURCE_PREFIX}${caseSlug}`;
}

/**
 * ### isInquiryCtaSource
 *
 * Reports whether a value is a fixed source value or a well-formed case source.
 *
 * @param value Value of unverified shape, typically a query parameter or request body field
 * @returns Whether the value can be treated as InquiryCtaSource
 */
export function isInquiryCtaSource(value: unknown): value is InquiryCtaSource {
  if (typeof value !== 'string') return false;

  return (
    INQUIRY_CTA_SOURCE_VALUES.some((source) => source === value) ||
    industrialCaseCtaSourcePattern.test(value)
  );
}

/**
 * ### parseInquiryCtaSource
 *
 * Resolves the `?source=` query parameter to a source value.
 * Anything that is not a single recognised string resolves to `undefined`, so an unknown
 * or tampered value is dropped rather than recorded.
 *
 * @param value Raw query value as exposed by the router
 * @returns The recognised source, or `undefined` when none applies
 */
export function parseInquiryCtaSource(
  value: unknown,
): InquiryCtaSource | undefined {
  return isInquiryCtaSource(value) ? value : undefined;
}

/**
 * ### solutionIdForInquiryCtaSource
 *
 * Derives the solution a lead belongs to from the call-to-action source.
 * Sources that do not start with a solution prefix, such as the home hero, yield `undefined`.
 *
 * @param source Source value carried by the inquiry, if any
 * @returns The solution identifier for analytics, or `undefined` when the source names none
 */
export function solutionIdForInquiryCtaSource(
  source: InquiryCtaSource | undefined,
): SolutionId | undefined {
  if (source?.startsWith('industrial-')) return 'listen_ai_industrial';
  if (source?.startsWith('safety-')) return 'listen_ai_safety';
  return undefined;
}

/**
 * ### InquiryHrefParams
 *
 * Query parameters a call-to-action may pre-fill on the inquiry page.
 */
export interface InquiryHrefParams {
  /**
   * ### interest
   *
   * Interest to pre-select in the form. Omitted when the visitor should choose.
   */
  interest?: InquiryInterest;

  /**
   * ### source
   *
   * Call-to-action that sent the visitor, recorded with the lead and its analytics events.
   */
  source?: InquiryCtaSource;
}

/**
 * ### buildInquiryHref
 *
 * Builds the locale-independent path of the inquiry page with the given pre-fill parameters.
 * Locale prefixing is left to the link component that renders the href.
 *
 * @param params Interest and source to attach; either may be omitted
 * @returns The inquiry page path with a query string, or the bare path when nothing is attached
 */
export function buildInquiryHref({
  interest,
  source,
}: InquiryHrefParams = {}): string {
  const searchParams = new URLSearchParams();
  if (interest) searchParams.set('interest', interest);
  if (source) searchParams.set('source', source);

  const query = searchParams.toString();
  return query ? `/inquiry?${query}` : '/inquiry';
}
